/**
 * Response Processor Types
 *
 * Core interfaces for the Strategy Pattern implementation of agent response processing.
 * This pattern allows different agents to have specialized response handling without
 * modifying the main sendMessage function.
 *
 * @module services/responseProcessors/types
 */

import { ChatContext } from '@/models/agentActions';
import { ChatMessageResponse, ContentItem } from '@/collections/chat';
import { StreamingEvent, StreamingEventType } from '@/types/agents';
import Vue from 'vue';

// Re-export ChatContext for convenience
export type { ChatContext };

// ============================================================================
// Normalized Response Types
// ============================================================================

/**
 * Normalized response from any format (old string, ChatResponse, PO Agent)
 *
 * Processors normalize all response formats into this structure for consistent handling.
 */
export interface NormalizedResponse {
  /** The text content to display in chat (filtered of control messages) */
  messageContent: string;

  /** Events extracted from new ChatResponse format */
  events: StreamingEvent[];

  /** Metadata from response (may contain action, payload, etc.) */
  metadata: Record<string, any>;

  /** Whether response uses new ChatResponse format (messages/events arrays) */
  isNewFormat: boolean;

  /** Regular messages to display (for agents with multiple messages like PO Agent) */
  regularMessages: Array<{
    content: string;
    agent?: string;
  }>;

  /** Action/control messages extracted (for PO Agent control messages) */
  actionMessages: Array<{
    action: string;
    parameters: any;
    agent?: string;
  }>;
}

// ============================================================================
// Structured Content Types (for custom UI rendering in ChatConversation.vue)
// ============================================================================

/**
 * Live chat button content - renders a styled button to connect to Crisp
 */
export interface LiveChatButtonContent {
  type: 'live_chat_button';
  buttonText: string;
  buttonDescription: string;
  ticketKey?: string;
  metadata: Record<string, any>;
}

/**
 * Data table content - renders a table with headers and rows
 */
export interface DataTableContent {
  type: 'datatable';
  headers: string[];
  rows: string[][];
}

/**
 * Chart content - renders a chart visualization
 */
export interface ChartContent {
  type: 'chart';
  chartType: string;
  data: any;
}

/**
 * Custom component content - for future extensibility
 */
export interface CustomComponentContent {
  type: string;
  [key: string]: any;
}

/**
 * Union type of all supported structured content types
 */
export type StructuredContentItem =
  | LiveChatButtonContent
  | DataTableContent
  | ChartContent
  | CustomComponentContent;

// ============================================================================
// Chat Message Types (matches AgentChatbox.vue ChatMessage interface)
// ============================================================================

/**
 * Chat message structure for conversation display
 */
export interface ChatMessage {
  /** Unique message ID */
  id: string;

  /** Message text content */
  content: string;

  /** Message timestamp */
  timestamp: Date;

  /** Whether this is a user message (vs assistant/system) */
  isUser: boolean;

  /** Agent info for assistant messages */
  agent?: {
    name: string;
    avatar: string;
  };

  /** Optional structured content for custom UI rendering */
  structuredContent?: StructuredContentItem[];

  /** Whether message is currently streaming */
  isStreaming?: boolean;
}

/**
 * Agent interface (matches AgentChatbox.vue Agent interface)
 */
export interface Agent {
  id: string;
  name: string;
  description: string;
  avatar: string;
  category: string;
  isRecommended: boolean;
  isFavorite: boolean;
  creator: string;
}

// ============================================================================
// Processing Result Types
// ============================================================================

/**
 * Result returned from processor after processing a response
 */
export interface ProcessingResult {
  /** Messages to add to conversation */
  messagesToAdd: ChatMessage[];

  /** Message IDs to remove from conversation */
  messagesToRemove: string[];

  /** Route navigation (for agent switching) */
  routeNavigation?: {
    targetAgentId: string;
    message?: string;
  };

  /** EventBus events to emit (for external actions like report preview, handoff) */
  eventBusEmits?: Array<{
    event: string;
    payload: any;
  }>;

  /** Whether processing was successful */
  success: boolean;

  /** Error message if not successful */
  error?: string;
}

// ============================================================================
// Processor Context Types
// ============================================================================

/**
 * Context provided to processors during execution
 *
 * Extends ChatContext with additional information needed for processing.
 */
export interface ProcessorContext extends ChatContext {
  /** The selected agent */
  selectedAgent: Agent;

  /** Current conversation ID */
  conversationId: string;

  /** Current conversation messages */
  currentConversation: ChatMessage[];

  /** Whether this is a continued conversation */
  isContinuedConversation: boolean;

  /** Conversation start time for analytics */
  conversationStartTime: number;

  /** Vue instance for $router, $route, $nextTick, $forceUpdate */
  vueInstance: Vue;

  /** Function to generate unique message IDs */
  generateMessageId: () => string;
}

// ============================================================================
// Response Processor Interface (Strategy Pattern)
// ============================================================================

/**
 * Response Processor Strategy Interface
 *
 * Each agent type implements this interface to handle its specific
 * response format and processing logic.
 *
 * @example
 * ```typescript
 * class ReportBuilderProcessor implements IResponseProcessor {
 *   readonly agentIds = ['report-builder'];
 *   readonly description = 'Processor for Report Builder agent';
 *
 *   normalizeResponse(response) { ... }
 *   async processResponse(normalized, context) { ... }
 * }
 * ```
 */
export interface IResponseProcessor {
  /** Agent IDs this processor handles (e.g., ['report-builder'] or ['purchase-order', 'po-agent']) */
  readonly agentIds: string[];

  /** Human-readable description for debugging */
  readonly description: string;

  /**
   * Normalize raw API response into standard format
   *
   * Handles different response formats (old string, old items, new ChatResponse)
   * and extracts message content, events, metadata, and action messages.
   *
   * @param response - Raw API response from ChatCollection.sendMessage()
   * @returns Normalized response structure
   */
  normalizeResponse(response: ChatMessageResponse): NormalizedResponse;

  /**
   * Process the normalized response and produce result
   *
   * Handles agent-specific logic like action detection, analytics tracking,
   * and generating messages with structured content.
   *
   * @param normalized - Normalized response from normalizeResponse()
   * @param context - Processing context with conversation state and utilities
   * @returns Processing result with messages to add/remove and events to emit
   */
  processResponse(
    normalized: NormalizedResponse,
    context: ProcessorContext
  ): Promise<ProcessingResult>;

  /**
   * Optional post-processing after main processing
   *
   * Called after applyProcessingResult() to perform cleanup or additional
   * actions that depend on the result being applied.
   *
   * @param result - The processing result that was applied
   * @param context - Processing context
   */
  postProcess?(
    result: ProcessingResult,
    context: ProcessorContext
  ): Promise<void>;
}

// ============================================================================
// Type Guards
// ============================================================================

/**
 * Type guard to check if content is a LiveChatButtonContent
 */
export function isLiveChatButtonContent(
  content: StructuredContentItem
): content is LiveChatButtonContent {
  return content.type === 'live_chat_button';
}

/**
 * Type guard to check if content is a DataTableContent
 */
export function isDataTableContent(
  content: StructuredContentItem
): content is DataTableContent {
  return content.type === 'datatable';
}

/**
 * Type guard to check if content is a ChartContent
 */
export function isChartContent(
  content: StructuredContentItem
): content is ChartContent {
  return content.type === 'chart';
}

// ============================================================================
// PO Agent Action Types
// ============================================================================

/**
 * Valid PO Agent action types
 */
export const PO_ACTION_TYPES = [
  'DISPLAY_SUPPLIERS_LIST',
  'DISPLAY_BUYING_TABLE',
  'DISPLAY_PURCHASE_ORDER',
  'DISPLAY_PURCHASE_ORDER_LIST',
  'DISPLAY_REORDER_SUGGESTIONS',
  'DISPLAY_PO_DRAFT',
  'DISPLAY_DRAFT_PO_PREVIEW',
  'DISPLAY_PRODUCT_ANALYSIS',
  'DISPLAY_RECOMMENDATION_EXPLANATION',
] as const;

export type POActionType = typeof PO_ACTION_TYPES[number];

/**
 * Type guard to check if action is a valid PO action
 */
export function isPOActionType(action: string): action is POActionType {
  return PO_ACTION_TYPES.includes(action as POActionType);
}
